import { getProductDetailData } from 'shared/api/product-details';
import { ProductInfo, ProductDetails, ComparisonData, ComparisonFeature, ComparisonCategory } from './types';

// Extract product info from API response
export const extractImportantInfo = (apiData: any): ProductInfo | null => {
  const product = apiData?.response?.product;
  if (!product) return null;

  // Extract attributes from pdp_sections
  const attributes: any = {};
  const pdpSections = product.pdp_sections || [];
  pdpSections.forEach((section: any) => {
    if (section.widget_type === 'parent_widget') {
      section.child_widgets?.forEach((widget: any) => {
        if (widget.widget_type === 'attribute_columnize_widget') {
          widget.attributes?.forEach((attr: any) => {
            attributes[attr.label] = attr.value;
          });
        }
        if (widget.widget_type === 'attribute_widget') {
          widget.attributes?.forEach((attr: any) => {
            attributes[attr.label] = attr.value;
          });
        }
        if (widget.widget_type === 'plain_widget') {
          if (widget.title) {
            attributes[widget.title] = widget.value;
          }
        }
      });
    }
    if (section.widget_type === 'plain_collapsable_widget' && section.title === 'Know your product') {
      attributes['Description'] = section.value;
    }
  });

  // Extract delivery details
  const deliveryDetails = product.delivery_details || {};
  const deliveryInfo = {
    isFreeDelivery: deliveryDetails.is_free_delivery || false,
    deliveryText: deliveryDetails.text || 'N/A',
    deliveryEnabled: deliveryDetails.enable || false,
  };

  // Extract review and rating data
  const reviewRating = product.review_rating_json || {};
  const reviewInfo = {
    starRating: reviewRating.star_rating || 0,
    reviewCount: reviewRating.review_count_v2 || reviewRating.review_count || 0,
    ratingCount: reviewRating.star_rating_count || 0,
    topReview: reviewRating.top_reviews_v2?.[0] || reviewRating.top_reviews?.[0] || null,
    showRatings: reviewRating.show_ratings || false,
  };

  // Extract all size options from sizeOptions.options
  const sizeOptionsData = product.sizeOptions?.options || [];
  const sizeOptions = sizeOptionsData.map((s: any) => s.sizeName || s.optionValue || s.title).filter(Boolean);

  // Extract COD
  const codAvailable = product.cash_on_delivery?.enable || false;

  return {
    id: product.id,
    title: product.title,
    subTitle: product.subTitle,
    price: product.price,
    discountedPrice: product.discountedPrice,
    discount: product.discount,
    imageUrl: product.imageUrl,
    color: product.color?.name,
    colorCode: product.color?.hex_code,
    material: attributes['Material'],
    pattern: attributes['Pattern'],
    occasion: attributes['Occasion'],
    fit: attributes['Fit'],
    neckline: attributes['Neckline'],
    sleeveStyle: attributes['Sleeve Style'],
    closure: attributes['Closure'],
    packSize: attributes['Pack Size'],
    dressShape: attributes['Dress Shape'],
    careInstructions: attributes['Care Instructions'] || attributes['care_instructions'],
    packContains: attributes['Pack Contains'],
    countryOfOrigin: attributes['Country of Origin'],
    netQuantity: attributes['Net Quantity'],
    description: attributes['Description'],
    returnPeriod: product.return_period,
    isReturnable: product.isReturnable,
    shipsIn: product.shipsIn,
    brand: product.aboutDesigner?.name,
    brandDescription: product.aboutDesigner?.description,
    tags: product.tag_list?.map((t: any) => t.title).join(', '),
    delivery: deliveryInfo,
    reviews: reviewInfo,
    codAvailable,
    sizeOptions,
  };
};

// Extract details for display
export const extractProductDetails = (productInfo: ProductInfo): ProductDetails => {
  const colorValue =
    typeof productInfo.color === 'object' && productInfo.color !== null
      ? (productInfo.color as any).name || 'N/A'
      : productInfo.color || 'N/A';

  return {
    productId: productInfo.id || '',
    title: productInfo.subTitle || productInfo.title || 'Not found',
    brand: productInfo.brand || productInfo.title || 'N/A',
    price: productInfo.price ? `₹${productInfo.price.toLocaleString()}` : 'N/A',
    discountedPrice: productInfo.discountedPrice
      ? `₹${productInfo.discountedPrice.toLocaleString()}`
      : null,
    discount: productInfo.discount ? `${productInfo.discount}% OFF` : null,
    color: colorValue,
    colorCode: productInfo.colorCode || '',
    material: productInfo.material || 'N/A',
    pattern: productInfo.pattern || 'N/A',
    fit: productInfo.fit || 'N/A',
    occasion: productInfo.occasion || 'N/A',
    returnPeriod: productInfo.returnPeriod ? `${productInfo.returnPeriod} days` : 'N/A',
    shipsIn: productInfo.shipsIn ? `${productInfo.shipsIn} days` : 'N/A',
    image: productInfo.imageUrl || '',
    tags: productInfo.tags || '',
    deliveryText: productInfo.delivery?.deliveryText || 'N/A',
    isFreeDelivery: productInfo.delivery?.isFreeDelivery || false,
    codAvailable: productInfo.codAvailable || false,
    sizeOptions: productInfo.sizeOptions?.join(', ') || 'N/A',
    starRating: productInfo.reviews?.starRating || 0,
    reviewCount: productInfo.reviews?.reviewCount || 0,
    topReview: productInfo.reviews?.topReview || null,
  };
};

// Fetch product by ID
export const fetchProductById = async (productID: string): Promise<ProductInfo | null> => {
  try {
    const response = await getProductDetailData({
      productID,
      isNew: true,
      headers: {},
      isMobile: false
    });
    
    if (response.status !== 'success') {
      throw new Error('API returned error status');
    }

    return extractImportantInfo(response);
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

// Create static comparison from product data — organized by categories
export const createStaticComparison = (products: ProductInfo[]): ComparisonData => {
  // Helper to safely get value or return "N/A"
  const getValue = (product: ProductInfo, key: keyof ProductInfo, formatter?: (val: any) => string): string => {
    const value = product[key];
    if (value === undefined || value === null || value === '') return 'N/A';
    return formatter ? formatter(value) : String(value);
  };

  // Helper to check if values differ across products
  const hasDifference = (values: string[]): boolean => {
    const normalized = values.map(v => v.toLowerCase().trim());
    return new Set(normalized).size > 1;
  };

  // Build a feature row with isDifferent flag
  const buildFeature = (feature: string, values: string[]): ComparisonFeature => ({
    feature,
    values,
    isDifferent: hasDifference(values),
  });

  // Filter out rows where all values are empty
  const filterEmpty = (features: ComparisonFeature[]): ComparisonFeature[] =>
    features.filter(row =>
      !row.values.every(val => val === 'N/A' || val === 'No' || val === 'No ratings' || val === 'No reviews' || val === '')
    );

  // --- Price & Value ---
  const priceFeatures = filterEmpty([
    buildFeature('Price', products.map(p => getValue(p, 'price', v => `₹${Number(v).toLocaleString()}`))),
    buildFeature('Discounted Price', products.map(p => getValue(p, 'discountedPrice', v => `₹${Number(v).toLocaleString()}`))),
    buildFeature('Discount', products.map(p => getValue(p, 'discount', v => `${v}% OFF`))),
  ]);

  // --- Appearance ---
  const appearanceFeatures = filterEmpty([
    buildFeature('Color', products.map(p => getValue(p, 'color'))),
    buildFeature('Material', products.map(p => getValue(p, 'material'))),
    buildFeature('Pattern', products.map(p => getValue(p, 'pattern'))),
    buildFeature('Fit', products.map(p => getValue(p, 'fit'))),
    buildFeature('Neckline', products.map(p => getValue(p, 'neckline'))),
    buildFeature('Sleeve Style', products.map(p => getValue(p, 'sleeveStyle'))),
    buildFeature('Closure', products.map(p => getValue(p, 'closure'))),
    buildFeature('Dress Shape', products.map(p => getValue(p, 'dressShape'))),
    buildFeature('Occasion', products.map(p => getValue(p, 'occasion'))),
  ]);

  // --- Size & Availability ---
  const sizeFeatures = filterEmpty([
    buildFeature('Available Sizes', products.map(p => p.sizeOptions?.join(', ') || 'N/A')),
    buildFeature('Pack Size', products.map(p => getValue(p, 'packSize'))),
    buildFeature('Pack Contains', products.map(p => getValue(p, 'packContains'))),
    buildFeature('Net Quantity', products.map(p => getValue(p, 'netQuantity'))),
  ]);

  // --- Delivery & Returns ---
  const deliveryFeatures = filterEmpty([
    buildFeature('Free Delivery', products.map(p => p.delivery?.isFreeDelivery ? 'Yes' : 'No')),
    buildFeature('Ships In', products.map(p => getValue(p, 'shipsIn', v => `${v} days`))),
    buildFeature('Return Period', products.map(p => getValue(p, 'returnPeriod', v => `${v} days`))),
    buildFeature('Returnable', products.map(p => p.isReturnable ? 'Yes' : 'No')),
    buildFeature('COD Available', products.map(p => p.codAvailable ? 'Yes' : 'No')),
  ]);

  // --- Ratings & Reviews ---
  const ratingFeatures = filterEmpty([
    buildFeature('Rating', products.map(p => {
      const rating = p.reviews?.starRating;
      return rating ? `${rating} ⭐` : 'No ratings';
    })),
    buildFeature('Review Count', products.map(p => {
      const count = p.reviews?.reviewCount;
      return count ? `${count} reviews` : 'No reviews';
    })),
  ]);

  // --- Additional Info ---
  const additionalFeatures = filterEmpty([
    buildFeature('Brand', products.map(p => getValue(p, 'brand'))),
    buildFeature('Care Instructions', products.map(p => getValue(p, 'careInstructions'))),
    buildFeature('Country of Origin', products.map(p => getValue(p, 'countryOfOrigin'))),
    buildFeature('Tags', products.map(p => getValue(p, 'tags'))),
  ]);

  // Build categories, filtering out empty ones
  const categories: ComparisonCategory[] = [
    { category: 'Price & Value', features: priceFeatures, isExpanded: true },
    { category: 'Appearance & Style', features: appearanceFeatures, isExpanded: true },
    { category: 'Size & Availability', features: sizeFeatures, isExpanded: true },
    { category: 'Delivery & Returns', features: deliveryFeatures, isExpanded: true },
    { category: 'Ratings & Reviews', features: ratingFeatures, isExpanded: true },
    { category: 'Additional Info', features: additionalFeatures, isExpanded: false },
  ].filter(cat => cat.features.length > 0);

  return { categories };
};

// Parse product ID from URL
export const parseProductId = (input: string): string | null => {
  if (input.startsWith('http')) {
    const match = input.match(/\/p\/(\d+)/);
    return match ? match[1] : null;
  }
  // Assume it's just a product ID
  return input.trim() || null;
};
